import type { DemoDoc } from '@mado/schema';
import { applyEdition, parseEdition, pickEdition, stepInEdition } from '@mado/schema/edition';
import { parseVarsJson } from './personalize';
import { Player, type PlayerOptions } from './runtime';

/**
 * Public entry for the player bundle.
 *
 * Note what is NOT here: schema validation. Documents are validated at publish
 * time, where a failure is a build error we can fix, rather than in the viewer,
 * where it would cost every visitor ~14KB of zod to discover a problem they
 * cannot do anything about.
 */

export { Player } from './runtime';
export type { PlayerOptions } from './runtime';

export function mount(host: HTMLElement, opts: PlayerOptions): Player {
  return new Player(host, opts);
}

/** `?s=<stepId>` deep link — required for linking a docs article to one step. */
function stepFromUrl(): string | null {
  return queryParam('s');
}

function queryParam(name: string): string | null {
  try {
    return new URL(location.href).searchParams.get(name);
  } catch {
    return null;
  }
}

/**
 * <mado-demo src="/d/abc.json"> or <mado-demo> with an inline
 * <script type="application/json"> child. The inline form is what the share page
 * uses, so the document arrives in the same round trip as the HTML.
 */
class MadoDemoElement extends HTMLElement {
  private player: Player | null = null;
  /** Waiting for the box to have a size before the edition is picked (`untilSized`). */
  private sizing: (() => void) | null = null;

  async connectedCallback(): Promise<void> {
    if (this.player || this.sizing) return;
    const loaded = await this.loadDoc();
    if (!loaded) return;

    this.style.display = 'block';
    // Phone edition or desktop steps, chosen once, here (`@mado/schema/edition`): forced by
    // `<mado-demo edition="phone">` or `?edition=phone`, else by the shape of the box the
    // demo was given. A box with no size yet says nothing about its shape, and neither does
    // the window then (an iframe not yet sized is 0x0 too: the founder's iPhone got the
    // desktop steps, 2026-10-16): the pick, and the player, wait for a real size. Nothing is
    // on screen in a box without one, so nothing is shown late, and the viewer cannot have
    // started, so the edition never changes under them.
    const forced = parseEdition(this.getAttribute('edition') ?? queryParam('edition'));
    if (loaded.doc.phone && !forced && !this.box()) {
      await this.untilSized();
      if (this.player || !this.isConnected) return;
    }
    const box = this.box() ?? { w: window.innerWidth, h: window.innerHeight };
    const edition = pickEdition(loaded.doc, box, forced);
    this.dataset.edition = edition;
    this.player = new Player(this, {
      doc: applyEdition(loaded.doc, edition),
      endpoint: this.getAttribute('endpoint'),
      startStep: stepInEdition(loaded.doc, this.getAttribute('step') ?? stepFromUrl(), edition),
      assetBase: this.resolveAssetBase(loaded.origin),
      fit: this.getAttribute('fit') === 'fill' ? 'fill' : null,
      vars: parseVarsJson(this.getAttribute('vars')),
      // `<mado-demo autoplay>`: watch mode starts once the player is on
      // screen. Set by the loader's `data-autoplay` and by `?play=1` on the
      // share / embed routes (apps/web). Off unless asked for.
      autoplay: this.hasAttribute('autoplay'),
      // `<mado-demo for="acme">` (set by apps/web from `?for=`, and by the
      // loader from `data-for`), else `?for=` on this page itself.
      label: this.getAttribute('for') ?? queryParam('for'),
      // `<mado-demo hub="hk_…">`: set by apps/web's `/e/:slug` from `?hub=`.
      hub: this.getAttribute('hub'),
    });
  }

  disconnectedCallback(): void {
    this.sizing?.();
    this.player?.destroy();
    this.player = null;
  }

  /** The box's size, or null while it has none (not laid out, hidden, an iframe not yet sized). */
  private box(): { w: number; h: number } | null {
    const r = this.getBoundingClientRect();
    return r.width > 0 && r.height > 0 ? { w: r.width, h: r.height } : null;
  }

  /**
   * Resolves once the box has a size: told by its ResizeObserver or by the window's own resize
   * (an iframe's box settling; either alone has been missed on iOS), or when the element leaves
   * the page (`connectedCallback` then mounts nothing).
   */
  private untilSized(): Promise<void> {
    return new Promise((resolve) => {
      const check = (): void => {
        if (this.box()) done();
      };
      const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(check) : null;
      const done = (): void => {
        ro?.disconnect();
        window.removeEventListener('resize', check);
        this.sizing = null;
        resolve();
      };
      this.sizing = done;
      ro?.observe(this);
      window.addEventListener('resize', check);
    });
  }

  /**
   * `/a/...` asset URLs inside the document must resolve against wherever the
   * document itself came from, not against this element's host page. When it
   * was fetched via `src`, that origin is unambiguous. An inline JSON document
   * has no such URL, so it needs an explicit `asset-base` attribute — falling
   * back to this page's own origin, which is correct when the inline document
   * was rendered by the share page itself (same origin as `/a/*`).
   */
  private resolveAssetBase(fetchedFrom: string | null): string | null {
    const explicit = this.getAttribute('asset-base');
    if (explicit) return explicit;
    if (fetchedFrom) return fetchedFrom;
    return typeof location !== 'undefined' ? location.origin : null;
  }

  private async loadDoc(): Promise<{ doc: DemoDoc; origin: string | null } | null> {
    const inline = this.querySelector('script[type="application/json"]');
    if (inline?.textContent) {
      try {
        return { doc: JSON.parse(inline.textContent) as DemoDoc, origin: null };
      } catch {
        return null;
      }
    }
    const src = this.getAttribute('src');
    if (!src) return null;
    try {
      const res = await fetch(src, { credentials: 'omit' });
      if (!res.ok) return null;
      const doc = (await res.json()) as DemoDoc;
      let origin: string | null = null;
      try {
        origin = new URL(src, location.href).origin;
      } catch {
        origin = null;
      }
      return { doc, origin };
    } catch {
      return null;
    }
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('mado-demo')) {
  customElements.define('mado-demo', MadoDemoElement);
}

declare global {
  interface Window {
    Mado?: { mount: typeof mount };
  }
}
// Merged, not assigned: on a page that also runs `help.js` (src/help.ts),
// `window.Mado` already carries `open` / `hub` / `on`, whichever loaded first.
if (typeof window !== 'undefined') window.Mado = Object.assign(window.Mado ?? {}, { mount });
